import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { cssMap } from '@compiled/react';
import { defineMessages, useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { getDocument } from '@atlaskit/browser-apis';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box } from '@atlaskit/primitives/compiled';
import Select, { components, type ValueType } from '@atlaskit/select';
import type { AtlaskitSelectRefType } from '@atlaskit/select/types';
import { layers } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { fireUIAnalytics } from '@atlassian/analytics-bridge';
import { OptionTag } from '@atlassian/option-color-picker/OptionTag';

import type { FieldBaseProps } from '../common/types';

import type { JiraSingleSelectField_Platform$key } from './__generated__/JiraSingleSelectField_Platform.graphql';

const messages = defineMessages({
	selectLabel: {
		id: 'jira.jira.jira-platform-fields.single-select.label',
		defaultMessage: 'Select {fieldName}',
		description: 'Label for the Jira single select dropdown',
	},
	noOptionMessage: {
		id: 'jira.jira.jira-platform-fields.single-select.no.options',
		defaultMessage: 'None',
		description:
			'Message displayed when there is no option selected in the Jira single select dropdown',
	},
});

export type Props = FieldBaseProps & {
	field: JiraSingleSelectField_Platform$key;
	onChange?: (newValue: string | null) => void;
};

type SelectOption = {
	color: string;
	value: string;
};

const styles = cssMap({
	readViewContainer: {
		paddingLeft: token('space.100'),
		height: '40px',
	},
	control: {
		width: '100%',
		boxSizing: 'border-box',
		height: '40px',
	},
	container: {
		display: 'flex',
		flex: '1 1 0',
		minWidth: '0px',
		width: '100%',
	},
	valueContainer: {
		minWidth: '0px',
	},
});

const ReadView = ({ option }: { option: SelectOption }) => (
	<OptionTag name={option.value ?? ''} color={option.color ?? null} showTooltip />
);

/**
 * `JiraSingleSelectField` is a memoized React component that renders a Jira single select field,
 * supporting both editable and read-only modes. It displays the current option and allows
 * users to select from available options if the field is editable. The component uses
 * Relay fragments to fetch option data and renders options with icons and labels.
 *
 * @param field - The Relay fragment reference for the Jira option field.
 * @param fieldValue - The currently suggested option value.
 * @param onChange - Callback invoked when the option selection changes.
 * @returns A JSX element representing the option field, or `null` if no option is found.
 */

export const JiraSingleSelectField: React.MemoExoticComponent<
	({ fieldValue, field, isFieldEditable, onChange }: Props) => React.JSX.Element
> = memo(({ fieldValue, field, isFieldEditable = true, onChange }: Props): React.JSX.Element => {
	const data = useFragment<JiraSingleSelectField_Platform$key>(
		graphql`
			fragment JiraSingleSelectField_Platform on JiraSingleSelectField {
				__typename
				name
				fieldConfig {
					isEditable
					isRequired
				}
				fieldOptions {
					edges {
						node {
							value
							color {
								colorKey
							}
						}
					}
				}
				fieldOption {
					value
					color {
						colorKey
					}
				}
			}
		`,
		field,
	);

	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const selectRef = useRef<AtlaskitSelectRefType | null>(null);

	const { isRequired } = data.fieldConfig ?? {};

	const isEditable = !!data?.fieldConfig?.isEditable && isFieldEditable;

	const options: SelectOption[] = useMemo(() => {
		return (
			data?.fieldOptions?.edges?.filter(Boolean).map((edge) => ({
				value: String(edge?.node?.value ?? ''),
				color: edge?.node?.color?.colorKey ?? '',
			})) ?? []
		);
	}, [data?.fieldOptions?.edges]);

	const defaultValue = data?.fieldOption?.value;

	const selectedOption = useMemo(() => {
		if (fieldValue == null) {
			return null;
		}
		if (fieldValue) {
			const fieldOption = options.find((option) => option.value === fieldValue) ?? null;
			return fieldOption;
		}
	}, [fieldValue, options]);

	// Allows us to make sure we only fall back to default value once, on initial render
	const isFirstRenderRef = useRef(true);
	useEffect(() => {
		// If the passed down value is empty and a default value is available, tell the upstream
		if (!selectedOption?.value && defaultValue != null && isFirstRenderRef.current) {
			onChange?.(defaultValue);
		}
		isFirstRenderRef.current = false;
	}, [selectedOption?.value, defaultValue, onChange]);

	const handleChange = useCallback(
		(newValue: ValueType<SelectOption | null>) => {
			onChange?.(newValue?.value ?? null);
			// Defer blur so internal state settles before removing focus styling.
			queueMicrotask(() => selectRef.current?.blur?.());
		},
		[onChange],
	);

	const [isMenuOpen, setIsMenuOpen] = useState(false);

	const handleMenuClose = useCallback(() => {
		fireUIAnalytics(
			createAnalyticsEvent({
				action: 'cancelled',
				actionSubject: 'inlineEdit',
			}),
			{
				fieldName: data.name,
				fieldType: data.__typename,
				isRequired,
				isEditable,
			},
		);

		selectRef.current?.blur?.();
		setIsMenuOpen(false);
	}, [createAnalyticsEvent, data.name, data.__typename, isRequired, isEditable]);

	const handleMenuOpen = useCallback(() => {
		fireUIAnalytics(
			createAnalyticsEvent({
				action: 'initiated',
				actionSubject: 'inlineEdit',
			}),
			{
				fieldName: data.name,
				fieldType: data.__typename,
				isRequired,
				isEditable,
			},
		);

		setIsMenuOpen(true);
	}, [createAnalyticsEvent, data.name, data.__typename, isRequired, isEditable]);

	if (isEditable) {
		return (
			<Select<SelectOption>
				ref={selectRef}
				label={formatMessage(messages.selectLabel, { fieldName: data.name })}
				appearance="subtle"
				options={options}
				getOptionValue={(option) => option.value}
				getOptionLabel={(option) => option.value}
				value={selectedOption}
				onChange={handleChange}
				onMenuOpen={handleMenuOpen}
				onMenuClose={handleMenuClose}
				searchThreshold={5}
				isSearchable={false}
				isRequired={isRequired || false}
				isClearable={isMenuOpen && !isRequired}
				placeholder={formatMessage(messages.noOptionMessage)}
				formatOptionLabel={(option) => <ReadView option={option} />}
				components={{
					Control: (props) => <components.Control {...props} xcss={styles.control} />,
					SelectContainer: (props) => (
						<components.SelectContainer {...props} xcss={styles.container} />
					),
					ValueContainer: (props) => (
						<components.ValueContainer {...props} xcss={styles.valueContainer} />
					),
					DropdownIndicator: (props) =>
						props.selectProps.menuIsOpen ? <components.DropdownIndicator {...props} /> : null,
				}}
				isMulti={false}
				menuPortalTarget={
					fg('gravityai-4581-jira-aifc-iframe-korosu') ? undefined : getDocument()?.body
				}
				menuPosition={fg('gravityai-4581-jira-aifc-iframe-korosu') ? 'fixed' : 'absolute'}
				menuPlacement="auto"
				styles={
					fg('gravityai-4581-jira-aifc-iframe-korosu')
						? {
								menu: (base) => ({ ...base, zIndex: layers.modal() }),
								menuPortal: (base) => ({ ...base, zIndex: layers.modal() }),
							}
						: undefined
				}
			/>
		);
	}

	return (
		<Box xcss={styles.readViewContainer}>
			{selectedOption ? (
				<ReadView option={selectedOption} />
			) : (
				formatMessage(messages.noOptionMessage)
			)}
		</Box>
	);
});
